import type { GuideTourDefinition } from '../types'
import { GUIDE_TOUR_TYPES } from '../types'
import {
  GUIDE_TOUR_PRIORITY_OFFSET_MAX,
  GUIDE_TOUR_PRIORITY_OFFSET_MIN,
  tourIsSingleRoute,
} from '../routing/utils'
import {
  assertNonEmptyString,
  assertOptionalBoolean,
  assertUniqueValues,
} from './assertions'
import { GuideTourValidationError } from './errors'
import { validateOptionalGuideTourExpiresAt } from './expiresAt'
import { validateGuideTourObserverSelectors } from './observerSelectors'
import { validateGuideTourStep } from './validateGuideTourStep'
import { validateGuideTourCardSize } from './validateGuideTourCardSize'

const validateGuideTourPriority = (priority: number | undefined): void => {
  if (priority === undefined) {
    return
  }

  if (!Number.isInteger(priority)) {
    throw new GuideTourValidationError('priority must be an integer')
  }

  if (
    priority < GUIDE_TOUR_PRIORITY_OFFSET_MIN ||
    priority > GUIDE_TOUR_PRIORITY_OFFSET_MAX
  ) {
    throw new GuideTourValidationError(
      `priority must be between ${GUIDE_TOUR_PRIORITY_OFFSET_MIN} and ${GUIDE_TOUR_PRIORITY_OFFSET_MAX} (tie-breaker within the same tier)`
    )
  }
}

const validateGuideTourStepRoutes = (definition: GuideTourDefinition): void => {
  if (definition.route) {
    return
  }

  const missingRouteIndex = definition.steps.findIndex(step => !step.route)

  if (missingRouteIndex !== -1) {
    throw new GuideTourValidationError(
      `steps[${missingRouteIndex}].route is required when the tour has no top-level route`
    )
  }
}

const validateGuideTourDefinitionFlags = (
  definition: GuideTourDefinition
): void => {
  assertOptionalBoolean(definition.listInNotifications, 'listInNotifications')
  assertOptionalBoolean(definition.waitForModalHidden, 'waitForModalHidden')

  if (definition.waitForHidden !== undefined) {
    validateGuideTourObserverSelectors(
      definition.waitForHidden,
      'waitForHidden'
    )
  }

  assertOptionalBoolean(definition.autoScrollToTarget, 'autoScrollToTarget')

  validateGuideTourCardSize(definition.size, 'size')

  assertOptionalBoolean(definition.viewedOnStep, 'viewedOnStep')
  assertOptionalBoolean(definition.hideMobile, 'hideMobile')

  if (definition.guideTourButton === undefined) {
    return
  }

  assertOptionalBoolean(definition.guideTourButton, 'guideTourButton')

  if (definition.guideTourButton && !tourIsSingleRoute(definition)) {
    throw new GuideTourValidationError(
      'guideTourButton may only be true on single-route tours'
    )
  }
}

export function validateGuideTourDefinitionCore(
  definition: GuideTourDefinition
): void {
  assertNonEmptyString(definition.id, 'id')

  if (!GUIDE_TOUR_TYPES.includes(definition.type)) {
    throw new GuideTourValidationError(
      `type must be one of: ${GUIDE_TOUR_TYPES.join(', ')}`
    )
  }

  if (!Number.isInteger(definition.version) || definition.version < 1) {
    throw new GuideTourValidationError('version must be an integer >= 1')
  }

  if (definition.route !== undefined) {
    assertNonEmptyString(definition.route, 'route')
  }

  if (definition.campaignId !== undefined) {
    assertNonEmptyString(definition.campaignId, 'campaignId')
  }

  validateGuideTourPriority(definition.priority)
  validateGuideTourStepRoutes(definition)
  validateGuideTourDefinitionFlags(definition)
  validateOptionalGuideTourExpiresAt(definition.expiresAt, 'expiresAt')

  definition.steps.forEach((step, index) => {
    validateGuideTourStep(step, index)
  })
}

export function validateGuideTourRegistry(
  definitions: readonly GuideTourDefinition[]
): void {
  definitions.forEach(definition => {
    validateGuideTourDefinitionCore(definition)
  })

  assertUniqueValues(
    definitions.map(definition => definition.id),
    'tour id'
  )
}
